/**
 * PlatformEnterprises Page
 *
 * Management Console page for superadmins to manage enterprises
 * Enterprises are the top level of the hierarchy: Enterprise > Channel Partners > Organizations > Users
 *
 * Uses the shared DataTable component with inline action buttons.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { AnimatePresence } from 'framer-motion';
import {
  Building2,
  Plus,
  Edit2,
  Trash2,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  AlertTriangle,
  Users,
  Building,
  Link2,
  PlayCircle,
  PauseCircle,
  RefreshCw,
  X
} from 'lucide-react';
import { EnterpriseModal, EnterpriseFormData, Enterprise as EnterpriseBase } from '@/components/management/EnterpriseModal';
import { apiService } from '@/services/api';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { DataTable, ColumnDefinition, ActionDefinition, SortState, PaginationState } from '@/components/shared/DataTable';
import { FilterBar, FilterConfig } from '@/components/shared/FilterBar';
import { useFilterParams } from '@/hooks/useFilterParams';
import { BulkActionsBar, BulkAction } from '@/components/shared/BulkActionsBar';
import { ConfirmationDialog } from '@/components/ui/ConfirmationDialog';
import { Button } from '@/components/ui/button';
import { DeactivateConfirmModal, CascadeImpact } from '@/components/deletion';
import { useAuth } from '@/hooks/useAuth';

// Extend the modal's Enterprise type with page-specific required fields from the API response
interface Enterprise extends EnterpriseBase {
  id: string;
  slug: string;
  channelPartnerCount: number;
  organizationCount: number;
  createdAt: string;
}

interface ChannelPartner {
  id: string;
  name: string;
  slug: string;
  type: string;
  status: string;
  organizationCount: number;
  userCount: number;
}

// Filter configuration for FilterBar
const filterConfig: FilterConfig = {
  filters: [
    {
      key: 'search',
      type: 'search',
      label: 'Search',
      placeholder: 'Search enterprises...',
      width: 'lg',
    },
    {
      key: 'status',
      type: 'select',
      label: 'Status',
      placeholder: 'All Statuses',
      options: [
        { value: 'active', label: 'Active' },
        { value: 'pending', label: 'Pending' },
        { value: 'suspended', label: 'Suspended' },
        { value: 'cancelled', label: 'Cancelled' },
      ],
    },
    {
      key: 'type',
      type: 'select',
      label: 'Type',
      placeholder: 'All Types',
      options: [
        { value: 'strategic', label: 'Strategic' },
        { value: 'regional', label: 'Regional' },
        { value: 'specialty', label: 'Specialty' },
        { value: 'internal', label: 'Internal' },
      ],
    },
  ],
  showActiveChips: true,
  showClearAll: true,
};

const PlatformEnterprises: React.FC = () => {
  const navigate = useNavigate();
  const { isSuperAdmin } = useAuth();
  const [enterprises, setEnterprises] = useState<Enterprise[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingEnterprise, setEditingEnterprise] = useState<Enterprise | null>(null);

  // Deactivate state
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [deactivatingEnterprise, setDeactivatingEnterprise] = useState<Enterprise | null>(null);
  const [cascadeImpact, setCascadeImpact] = useState<CascadeImpact | undefined>(undefined);
  const [isDeactivating, setIsDeactivating] = useState(false);

  // Reactivate state
  const [showReactivateConfirm, setShowReactivateConfirm] = useState(false);
  const [reactivatingEnterprise, setReactivatingEnterprise] = useState<Enterprise | null>(null);

  // Hard delete state
  const [showHardDeleteModal, setShowHardDeleteModal] = useState(false);
  const [hardDeletingEnterprise, setHardDeletingEnterprise] = useState<Enterprise | null>(null);
  const [hardDeleteConfirmName, setHardDeleteConfirmName] = useState('');
  const [hardDeleteCheckbox, setHardDeleteCheckbox] = useState(false);
  const [isHardDeleting, setIsHardDeleting] = useState(false);

  // Error state for hard delete
  const [error, setError] = useState<string | null>(null);

  // Filter state with URL sync
  const { filters, setFilter, clearFilter, clearAllFilters, activeFilters } = useFilterParams<{
    search: string;
    status: string;
    type: string;
    orgStatus: string;
  }>({
    schema: {
      search: { type: 'string', default: '', debounce: 300 },
      status: { type: 'string', default: '' },
      type: { type: 'string', default: '' },
      orgStatus: { type: 'string', default: 'active' },
    },
    labels: {
      search: 'Search',
      status: 'Status',
      type: 'Type',
      orgStatus: 'Organization Status',
    },
  });

  // Pagination state
  const [pagination, setPagination] = useState<PaginationState>({
    page: 1,
    limit: 20,
    total: 0,
  });

  // Sorting state
  const [sortState, setSortState] = useState<SortState>({
    column: 'name',
    direction: 'asc',
  });

  // Selection state for bulk actions
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Assign Partners modal state
  const [showAssignPartnersModal, setShowAssignPartnersModal] = useState(false);
  const [assigningEnterprise, setAssigningEnterprise] = useState<Enterprise | null>(null);
  const [orphanPartners, setOrphanPartners] = useState<ChannelPartner[]>([]);
  const [loadingOrphanPartners, setLoadingOrphanPartners] = useState(false);

  const loadEnterprises = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('includeDeleted', 'true');
      if (filters.status) params.append('status', filters.status);
      if (filters.type) params.append('type', filters.type);

      interface EnterprisesResponse { success: boolean; data: { enterprises: Enterprise[]; totalCount: number } }
      const response = await apiService.get<EnterprisesResponse>(`/v1/platform/enterprises?${params.toString()}`);
      const allEnterprises = response.data?.enterprises || [];
      setEnterprises(allEnterprises);
      setPagination(prev => ({ ...prev, total: allEnterprises.length }));
    } catch (error) {
      console.error('Failed to load enterprises:', error);
      toast.error('Failed to load enterprises');
    } finally {
      setLoading(false);
    }
  }, [filters.status, filters.type]);

  useEffect(() => {
    loadEnterprises();
  }, [loadEnterprises]);

  const handleOpenCreateModal = () => {
    setEditingEnterprise(null);
    setShowCreateModal(true);
  };

  const handleEditEnterprise = useCallback((enterprise: Enterprise) => {
    setEditingEnterprise(enterprise);
    setShowCreateModal(true);
  }, []);

  const handleSaveEnterprise = async (data: EnterpriseFormData) => {
    if (editingEnterprise) {
      await apiService.put(`/v1/platform/enterprises/${editingEnterprise.id}`, data);
      toast.success('Enterprise updated successfully');
    } else {
      await apiService.post('/v1/platform/enterprises', data);
      toast.success('Enterprise created successfully');
    }
    setEditingEnterprise(null);
    loadEnterprises();
  };

  // Load orphan partners (without enterprise) for assign modal
  const loadOrphanPartners = async () => {
    setLoadingOrphanPartners(true);
    try {
      interface PartnersResponse { success: boolean; data: ChannelPartner[] }
      const response = await apiService.get<PartnersResponse>('/v1/platform/partners?noEnterprise=true');
      setOrphanPartners(response.data || []);
    } catch (error) {
      console.error('Failed to load orphan partners:', error);
      toast.error('Failed to load available partners');
    } finally {
      setLoadingOrphanPartners(false);
    }
  };

  const handleOpenAssignPartnersModal = useCallback((enterprise: Enterprise) => {
    setAssigningEnterprise(enterprise);
    setShowAssignPartnersModal(true);
    loadOrphanPartners();
  }, []);

  const handleAssignPartner = async (partnerId: string) => {
    if (!assigningEnterprise) return;

    try {
      await apiService.patch(`/v1/platform/partners/${partnerId}/assign-enterprise`, {
        enterpriseId: assigningEnterprise.id
      });
      toast.success('Partner assigned successfully');
      loadOrphanPartners();
      loadEnterprises();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to assign partner');
    }
  };

  const handleCloseAssignPartnersModal = () => {
    setShowAssignPartnersModal(false);
    setAssigningEnterprise(null);
    setOrphanPartners([]);
  };

  // Initiate deactivation - gets cascade impact for the confirmation modal
  const initiateDeactivate = useCallback(async (enterprise: Enterprise) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:8000'}/api/v1/platform/enterprises/${enterprise.id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({})
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to get deactivation info');
      }

      if (result.requiresConfirmation) {
        setDeactivatingEnterprise(enterprise);
        setCascadeImpact(result.cascadeImpact);
        setShowDeactivateModal(true);
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to initiate deactivation');
    }
  }, []);

  // Execute deactivation with name confirmation
  const handleDeactivateEnterprise = useCallback(async (confirmName: string) => {
    if (!deactivatingEnterprise) return;

    try {
      setIsDeactivating(true);
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:8000'}/api/v1/platform/enterprises/${deactivatingEnterprise.id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ confirmName })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to deactivate enterprise');
      }

      setShowDeactivateModal(false);
      setDeactivatingEnterprise(null);
      setCascadeImpact(undefined);
      toast.success('Enterprise deactivated successfully');
      loadEnterprises();
    } catch (error: any) {
      toast.error(error.message || 'Failed to deactivate enterprise');
    } finally {
      setIsDeactivating(false);
    }
  }, [deactivatingEnterprise, loadEnterprises]);

  // Reactivate enterprise
  const handleReactivateEnterprise = useCallback(async () => {
    if (!reactivatingEnterprise) return;
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:8000'}/api/v1/platform/enterprises/${reactivatingEnterprise.id}/reactivate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.error || 'Failed to reactivate enterprise');
      }
      setShowReactivateConfirm(false);
      setReactivatingEnterprise(null);
      toast.success('Enterprise reactivated successfully');
      loadEnterprises();
    } catch (err: any) {
      toast.error(err.message || 'Failed to reactivate enterprise');
    }
  }, [reactivatingEnterprise, loadEnterprises]);

  // Hard delete enterprise permanently
  const handleHardDelete = useCallback(async () => {
    if (!hardDeletingEnterprise?.id) return;
    const token = localStorage.getItem('accessToken');
    if (!token) return;
    setIsHardDeleting(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:8000'}/api/v1/platform/enterprises/${hardDeletingEnterprise.id}/permanent`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirmName: hardDeleteConfirmName, confirmCheckbox: hardDeleteCheckbox })
      });
      const result = await response.json();
      if (result.success) {
        setShowHardDeleteModal(false);
        setHardDeletingEnterprise(null);
        toast.success('Enterprise permanently deleted');
        loadEnterprises();
      } else {
        setError(result.error || 'Failed to permanently delete');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to permanently delete enterprise');
    } finally {
      setIsHardDeleting(false);
    }
  }, [hardDeletingEnterprise, hardDeleteConfirmName, hardDeleteCheckbox, loadEnterprises]);

  const handleUpdateStatus = useCallback(async (enterpriseId: string, newStatus: string) => {
    try {
      await apiService.put(`/v1/platform/enterprises/${enterpriseId}`, { status: newStatus });
      toast.success(`Enterprise ${newStatus === 'active' ? 'activated' : 'suspended'} successfully`);
      loadEnterprises();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to update enterprise');
    }
  }, [loadEnterprises]);

  // Bulk action handlers
  const handleBulkActivate = useCallback(async (ids: string[]) => {
    try {
      await Promise.all(ids.map(id => apiService.put(`/v1/platform/enterprises/${id}`, { status: 'active' })));
      toast.success(`${ids.length} enterprise${ids.length > 1 ? 's' : ''} activated`);
      setSelectedIds(new Set());
      loadEnterprises();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to activate enterprises');
    }
  }, [loadEnterprises]);

  const handleBulkSuspend = useCallback(async (ids: string[]) => {
    try {
      await Promise.all(ids.map(id => apiService.put(`/v1/platform/enterprises/${id}`, { status: 'suspended' })));
      toast.success(`${ids.length} enterprise${ids.length > 1 ? 's' : ''} suspended`);
      setSelectedIds(new Set());
      loadEnterprises();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to suspend enterprises');
    }
  }, [loadEnterprises]);

  const handleBulkDelete = useCallback(async (ids: string[]) => {
    try {
      await Promise.all(ids.map(id => apiService.delete(`/v1/platform/enterprises/${id}`)));
      toast.success(`${ids.length} enterprise${ids.length > 1 ? 's' : ''} deleted`);
      setSelectedIds(new Set());
      loadEnterprises();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to delete enterprises');
    }
  }, [loadEnterprises]);

  // Bulk actions configuration
  const bulkActions: BulkAction<Enterprise>[] = useMemo(() => [
    {
      id: 'activate',
      label: 'Activate',
      icon: <PlayCircle className="h-4 w-4" />,
      variant: 'success',
      onClick: handleBulkActivate,
      show: (selectedItems) => isSuperAdmin() && selectedItems.some(e => e.status !== 'active' && !e.isDeleted),
    },
    {
      id: 'suspend',
      label: 'Suspend',
      icon: <PauseCircle className="h-4 w-4" />,
      variant: 'warning',
      onClick: handleBulkSuspend,
      show: (selectedItems) => isSuperAdmin() && selectedItems.some(e => e.status === 'active' && !e.isDeleted),
    },
    {
      id: 'delete',
      label: 'Delete',
      icon: <Trash2 className="h-4 w-4" />,
      variant: 'danger',
      onClick: handleBulkDelete,
      confirm: {
        title: 'Delete Enterprises',
        message: 'Are you sure you want to delete the selected enterprises? This will also delete all channel partners, organizations, and deactivate all users.',
        confirmText: 'Delete',
      },
      show: (selectedItems) => isSuperAdmin() && selectedItems.some(e => !e.isDeleted),
    },
  ], [handleBulkActivate, handleBulkSuspend, handleBulkDelete, isSuperAdmin]);

  // Client-side search + status filtering
  const filteredEnterprises = useMemo(() => {
    const searchLower = (filters.search || '').toLowerCase();
    let result = enterprises;

    // Status tab filtering
    const statusFilter = (filters.orgStatus as string) || 'active';
    if (statusFilter === 'active') {
      result = result.filter(e => !e.isDeleted);
    } else if (statusFilter === 'deactivated') {
      result = result.filter(e => e.isDeleted);
    }

    if (searchLower) {
      result = result.filter(enterprise =>
        enterprise.name.toLowerCase().includes(searchLower) ||
        enterprise.slug.toLowerCase().includes(searchLower) ||
        enterprise.contact.primaryEmail.toLowerCase().includes(searchLower)
      );
    }

    return result;
  }, [enterprises, filters.search, filters.orgStatus]);

  // Sorted data
  const sortedEnterprises = useMemo(() => {
    if (!sortState.column || !sortState.direction) return filteredEnterprises;

    const getValue = (obj: any, path: string) =>
      path.split('.').reduce((acc, part) => acc?.[part], obj);

    return [...filteredEnterprises].sort((a, b) => {
      const aVal = getValue(a, sortState.column!);
      const bVal = getValue(b, sortState.column!);
      if (aVal == null) return sortState.direction === 'asc' ? 1 : -1;
      if (bVal == null) return sortState.direction === 'asc' ? -1 : 1;
      const cmp = typeof aVal === 'string'
        ? aVal.localeCompare(bVal)
        : Number(aVal) - Number(bVal);
      return sortState.direction === 'asc' ? cmp : -cmp;
    });
  }, [filteredEnterprises, sortState]);

  // Paginated data
  const paginatedEnterprises = useMemo(() => {
    const start = (pagination.page - 1) * pagination.limit;
    const end = start + pagination.limit;
    return sortedEnterprises.slice(start, end);
  }, [sortedEnterprises, pagination.page, pagination.limit]);

  // Update pagination total when filtered data changes
  useEffect(() => {
    setPagination(prev => ({ ...prev, total: filteredEnterprises.length, page: 1 }));
  }, [filteredEnterprises.length]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'suspended':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'cancelled':
        return <AlertCircle className="h-4 w-4 text-gray-500" />;
      default:
        return null;
    }
  };

  const getStatusBadge = (status: string) => {
    const classes = {
      active: 'bg-green-100 text-green-800',
      suspended: 'bg-red-100 text-red-800',
      pending: 'bg-yellow-100 text-yellow-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    return classes[status as keyof typeof classes] || 'bg-gray-100 text-gray-800';
  };

  const getTypeBadge = (type: string) => {
    const classes = {
      strategic: 'bg-purple-100 text-purple-800',
      regional: 'bg-blue-100 text-blue-800',
      specialty: 'bg-indigo-100 text-indigo-800',
      internal: 'bg-gray-100 text-gray-800'
    };
    return classes[type as keyof typeof classes] || 'bg-gray-100 text-gray-800';
  };

  // DataTable column definitions with explicit widths for table-fixed layout
  const columns: ColumnDefinition<Enterprise>[] = useMemo(() => [
    {
      id: 'name',
      header: 'Enterprise',
      sortable: true,
      sortKey: 'name',
      width: '25%',
      accessor: (enterprise) => (
        <div className="flex items-center min-w-0">
          <div className="flex-shrink-0 h-10 w-10 bg-purple-100 rounded-lg flex items-center justify-center">
            <Building2 className="h-5 w-5 text-purple-600" />
          </div>
          <div className="ml-4 min-w-0">
            <div className="text-sm font-medium text-gray-900 truncate">{enterprise.name}</div>
            <div className="text-sm text-gray-500 truncate">{enterprise.slug}</div>
          </div>
        </div>
      )
    },
    {
      id: 'type',
      header: 'Type',
      sortable: true,
      sortKey: 'type',
      width: '10%',
      hideOnTablet: true, // Only show on desktop (≥1024px)
      accessor: (enterprise) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getTypeBadge(enterprise.type)}`}>
          {enterprise.type}
        </span>
      )
    },
    {
      id: 'status',
      header: 'Status',
      sortable: true,
      sortKey: 'status',
      width: '14%',
      accessor: (enterprise) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getStatusBadge(enterprise.status)}`}>
          {getStatusIcon(enterprise.status)}
          <span className="ml-1">{enterprise.status}</span>
        </span>
      )
    },
    {
      id: 'channelPartners',
      header: 'Partners',
      sortable: true,
      sortKey: 'channelPartnerCount',
      width: '15%',
      accessor: (enterprise) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/management/partners?enterpriseId=${enterprise.id}`);
          }}
          className="flex items-center text-sm text-purple-600 hover:text-purple-800 hover:underline transition-colors"
        >
          <Users className="h-4 w-4 text-purple-400 mr-1 flex-shrink-0" />
          <span className="truncate">
            {enterprise.channelPartnerCount}
            {enterprise.quotas.maxChannelPartners !== -1 && (
              <span className="text-gray-400 ml-1">/ {enterprise.quotas.maxChannelPartners}</span>
            )}
          </span>
        </button>
      )
    },
    {
      id: 'organizations',
      header: 'Orgs',
      sortable: true,
      sortKey: 'organizationCount',
      width: '10%',
      hideOnTablet: true, // Only show on desktop (≥1024px)
      accessor: (enterprise) => (
        <div className="flex items-center text-sm text-gray-900">
          <Building className="h-4 w-4 text-gray-400 mr-1 flex-shrink-0" />
          {enterprise.organizationCount}
          {enterprise.quotas.maxOrganizationsTotal !== -1 && (
            <span className="text-gray-400 ml-1">/ {enterprise.quotas.maxOrganizationsTotal}</span>
          )}
        </div>
      )
    },
    {
      id: 'contact',
      header: 'Contact',
      sortable: true,
      sortKey: 'contact.primaryName',
      width: '18%',
      hideOnTablet: true, // Only show on desktop (≥1024px)
      accessor: (enterprise) => (
        <div className="min-w-0">
          <div className="text-sm text-gray-900 truncate">{enterprise.contact.primaryName}</div>
          <div className="text-sm text-gray-500 truncate" title={enterprise.contact.primaryEmail}>
            {enterprise.contact.primaryEmail}
          </div>
        </div>
      )
    }
  ], []);

  // DataTable action definitions (inline buttons)
  const actions: ActionDefinition<Enterprise>[] = useMemo(() => [
    {
      id: 'view',
      icon: <Eye className="h-4 w-4" />,
      tooltip: 'View details',
      onClick: (enterprise) => navigate(`/management/enterprises/${enterprise.id}`),
      variant: 'default'
    },
    {
      id: 'edit',
      icon: <Edit2 className="h-4 w-4" />,
      tooltip: 'Edit',
      onClick: (enterprise) => handleEditEnterprise(enterprise),
      variant: 'primary',
      show: (enterprise) => isSuperAdmin() && !enterprise.isDeleted
    },
    {
      id: 'assignPartners',
      icon: <Link2 className="h-4 w-4" />,
      tooltip: 'Assign Partners',
      onClick: (enterprise) => handleOpenAssignPartnersModal(enterprise),
      variant: 'default',
      show: (enterprise) => isSuperAdmin() && !enterprise.isDeleted
    },
    {
      id: 'suspend',
      icon: <XCircle className="h-4 w-4" />,
      tooltip: 'Suspend',
      onClick: (enterprise) => handleUpdateStatus(enterprise.id, 'suspended'),
      show: (enterprise) => isSuperAdmin() && !enterprise.isDeleted && enterprise.status === 'active',
      variant: 'warning'
    },
    {
      id: 'activate',
      icon: <CheckCircle className="h-4 w-4" />,
      tooltip: 'Activate',
      onClick: (enterprise) => handleUpdateStatus(enterprise.id, 'active'),
      show: (enterprise) => isSuperAdmin() && !enterprise.isDeleted && (enterprise.status === 'suspended' || enterprise.status === 'pending'),
      variant: 'success'
    },
    {
      id: 'deactivate',
      icon: <Trash2 className="h-4 w-4" />,
      tooltip: 'Deactivate',
      onClick: (enterprise) => initiateDeactivate(enterprise),
      show: (enterprise) => isSuperAdmin() && !enterprise.isDeleted,
      variant: 'danger'
    },
    {
      id: 'reactivate',
      icon: <RefreshCw className="h-4 w-4" />,
      tooltip: 'Reactivate',
      onClick: (enterprise) => {
        setReactivatingEnterprise(enterprise);
        setShowReactivateConfirm(true);
      },
      show: (enterprise) => isSuperAdmin() && enterprise.isDeleted === true,
      variant: 'success'
    },
    {
      id: 'permanentDelete',
      icon: <Trash2 className="h-4 w-4" />,
      tooltip: 'Permanently delete',
      onClick: (enterprise) => {
        setHardDeletingEnterprise(enterprise);
        setHardDeleteConfirmName('');
        setHardDeleteCheckbox(false);
        setShowHardDeleteModal(true);
      },
      show: (enterprise) => isSuperAdmin() && enterprise.isDeleted === true,
      variant: 'danger'
    }
  ], [navigate, handleUpdateStatus, handleEditEnterprise, handleOpenAssignPartnersModal, initiateDeactivate, isSuperAdmin]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Enterprises</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage enterprise accounts and their channel partners
          </p>
        </div>
        {isSuperAdmin() && (
          <button
            onClick={handleOpenCreateModal}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Enterprise
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <FilterBar
          config={filterConfig}
          values={filters}
          onChange={(key, value) => setFilter(key as keyof typeof filters, value as never)}
          onClear={(key) => clearFilter(key as keyof typeof filters)}
          onClearAll={clearAllFilters}
          activeFilters={activeFilters}
          loading={loading}
        />
      </div>

      {/* Status Tabs */}
      <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 w-fit">
        {[
          { value: 'active', label: 'Active', count: enterprises.filter(e => !e.isDeleted).length },
          { value: 'deactivated', label: 'Deactivated', count: enterprises.filter(e => e.isDeleted).length },
        ].map(tab => (
          <button
            key={tab.value}
            onClick={() => setFilter('orgStatus' as any, tab.value as never)}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              (filters.orgStatus || 'active') === tab.value
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
            {tab.count > 0 && (
              <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                (filters.orgStatus || 'active') === tab.value
                  ? tab.value === 'deactivated' ? 'bg-red-100 text-red-700' : 'bg-purple-100 text-purple-700'
                  : 'bg-gray-200 text-gray-600'
              }`}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Bulk Actions Bar */}
      <AnimatePresence>
        {selectedIds.size > 0 && (
          <BulkActionsBar
            selectedIds={selectedIds}
            items={filteredEnterprises}
            getItemId={(e) => e.id}
            actions={bulkActions}
            onClearSelection={() => setSelectedIds(new Set())}
            entityName="enterprise"
            theme="purple"
          />
        )}
      </AnimatePresence>

      {/* Table - Using shared DataTable component */}
      <DataTable
        data={paginatedEnterprises}
        columns={columns}
        actions={actions}
        loading={loading}
        emptyMessage="No enterprises found"
        emptyIcon={<Building2 className="h-12 w-12 text-gray-300" />}
        selectable
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        sortable
        sortState={sortState}
        onSortChange={setSortState}
        pagination={pagination}
        onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
        onLimitChange={(limit) => setPagination(prev => ({ ...prev, limit, page: 1 }))}
        rowClassName={(enterprise) => enterprise.isDeleted ? 'opacity-50' : ''}
        hoverable
        striped={false}
        ariaLabel="Enterprises table"
      />

      {/* Create/Edit Enterprise Modal */}
      <EnterpriseModal
        mode={editingEnterprise ? 'edit' : 'create'}
        enterprise={editingEnterprise}
        open={showCreateModal}
        onOpenChange={(open) => {
          setShowCreateModal(open);
          if (!open) setEditingEnterprise(null);
        }}
        onSave={handleSaveEnterprise}
      />

      {/* Deactivate Confirmation Modal */}
      <DeactivateConfirmModal
        isOpen={showDeactivateModal}
        entityType="enterprise"
        entityName={deactivatingEnterprise?.name || ''}
        cascadeImpact={cascadeImpact}
        isLoading={isDeactivating}
        onConfirm={handleDeactivateEnterprise}
        onCancel={() => {
          setShowDeactivateModal(false);
          setDeactivatingEnterprise(null);
          setCascadeImpact(undefined);
        }}
      />

      {/* Reactivate Confirmation Dialog */}
      <ConfirmationDialog
        open={showReactivateConfirm}
        onOpenChange={setShowReactivateConfirm}
        title="Reactivate Enterprise"
        message={
          reactivatingEnterprise ? (
            <span>
              Are you sure you want to reactivate <strong>{reactivatingEnterprise.name}</strong>?
              This will restore the enterprise and make it active again.
            </span>
          ) : ''
        }
        confirmText="Reactivate"
        variant="info"
        onConfirm={handleReactivateEnterprise}
      />

      {/* Hard Delete Confirmation Modal */}
      {showHardDeleteModal && hardDeletingEnterprise && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={() => setShowHardDeleteModal(false)} />
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg transform transition-all">
              <div className="flex items-center justify-between px-6 py-4 border-b border-red-200 bg-red-50 rounded-t-xl">
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full bg-red-100">
                    <AlertTriangle className="h-6 w-6 text-red-600" />
                  </div>
                  <h2 className="text-lg font-semibold text-red-900">Permanently Delete Enterprise</h2>
                </div>
              </div>
              <div className="px-6 py-5 space-y-4">
                {error && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                    {error}
                  </div>
                )}
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800">
                    <strong>This action is permanent and irreversible.</strong> All data including partners, organizations,
                    users, patients, clinical encounters, recordings, and medical notes will be permanently deleted.
                  </p>
                </div>
                <div className="space-y-2">
                  <label className="block text-sm text-gray-700">
                    To confirm, type{' '}
                    <span className="font-mono font-semibold bg-gray-100 px-1.5 py-0.5 rounded">{hardDeletingEnterprise.name}</span>
                    {' '}below:
                  </label>
                  <input
                    type="text"
                    value={hardDeleteConfirmName}
                    onChange={(e) => setHardDeleteConfirmName(e.target.value)}
                    placeholder="Type enterprise name to confirm"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors"
                    autoFocus
                    disabled={isHardDeleting}
                  />
                </div>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={hardDeleteCheckbox}
                    onChange={(e) => setHardDeleteCheckbox(e.target.checked)}
                    className="mt-0.5 h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                    disabled={isHardDeleting}
                  />
                  <span className="text-sm text-gray-700">
                    I understand this will permanently delete all enterprise data and this action cannot be undone
                  </span>
                </label>
              </div>
              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
                <button onClick={() => setShowHardDeleteModal(false)} disabled={isHardDeleting} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50">Cancel</button>
                <button
                  onClick={handleHardDelete}
                  disabled={hardDeleteConfirmName !== hardDeletingEnterprise.name || !hardDeleteCheckbox || isHardDeleting}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isHardDeleting ? 'Deleting...' : 'I understand the consequences, delete this enterprise'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assign Partners Modal */}
      {showAssignPartnersModal && assigningEnterprise && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Assign Partners</h2>
                  <p className="text-sm text-gray-500 mt-1">
                    Assign channel partners to <span className="font-medium">{assigningEnterprise.name}</span>
                  </p>
                </div>
                <button
                  onClick={handleCloseAssignPartnersModal}
                  className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="h-5 w-5 text-gray-400" />
                </button>
              </div>

              {/* Quota info */}
              <div className="bg-purple-50 rounded-lg p-3 mb-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-purple-700">Partner quota usage</span>
                  <span className="font-medium text-purple-900">
                    {assigningEnterprise.channelPartnerCount}
                    {assigningEnterprise.quotas.maxChannelPartners !== -1 && (
                      <span className="text-purple-600"> / {assigningEnterprise.quotas.maxChannelPartners}</span>
                    )}
                  </span>
                </div>
              </div>

              {/* Partner list */}
              <div className="space-y-2">
                <h3 className="text-sm font-medium text-gray-700">Available Partners (no enterprise assigned)</h3>

                {loadingOrphanPartners ? (
                  <div className="flex items-center justify-center py-8 text-gray-500">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-600 mr-2"></div>
                    Loading partners...
                  </div>
                ) : orphanPartners.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Users className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                    <p>No available partners without enterprise</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {orphanPartners.map((partner) => (
                      <div
                        key={partner.id}
                        className="flex items-center justify-between bg-gray-50 rounded-lg p-3 hover:bg-gray-100"
                      >
                        <div>
                          <div className="font-medium text-gray-900">{partner.name}</div>
                          <div className="text-xs text-gray-500">
                            {partner.type} • {partner.organizationCount} orgs • {partner.userCount} users
                          </div>
                        </div>
                        <button
                          onClick={() => handleAssignPartner(partner.id)}
                          className="px-3 py-1.5 text-sm font-medium text-purple-600 hover:text-purple-700 hover:bg-purple-50 rounded-md transition-colors"
                        >
                          Assign
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={handleCloseAssignPartnersModal}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlatformEnterprises;
